import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { SpurwechselLogo } from "@/components/SpurwechselLogo";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token") || "";

  // If token in URL → show password form directly. Otherwise → email form.
  const [step, setStep] = useState(tokenFromUrl ? "set_password" : "request");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Bitte geben Sie eine gültige E-Mail-Adresse ein");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setStep("email_sent");
    } catch {
      toast.error("Fehler beim Anfordern. Bitte versuchen Sie es erneut.");
    }
    setLoading(false);
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen lang sein");
      return;
    }
    if (password !== passwordConfirm) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: tokenFromUrl, new_password: password });
      setStep("done");
      toast.success("Passwort erfolgreich geändert!");
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.includes("abgelaufen")) {
        setStep("expired");
      } else {
        setStep("invalid");
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 page-transition" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <SpurwechselLogo size="default" className="justify-center mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold">Passwort zurücksetzen</h1>
        </div>
        <Card className="shadow-sm border-border/50">
          <CardContent className="pt-6 space-y-4">

            {/* Step 1: Enter email */}
            {step === "request" && (
              <form onSubmit={requestReset} className="space-y-4">
                <p className="font-body text-sm text-muted-foreground">
                  Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen Link zum Zurücksetzen Ihres Passworts.
                </p>
                <div>
                  <Label className="font-body text-sm">E-Mail</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="mt-1 h-11 font-body" placeholder="ihre@email.de" required data-testid="reset-email-input" />
                </div>
                <Button type="submit" className="w-full h-11 font-body bg-primary text-primary-foreground hover:bg-primary/90" disabled={loading} data-testid="request-reset-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Link anfordern
                </Button>
              </form>
            )}

            {/* Step 2: Email sent confirmation */}
            {step === "email_sent" && (
              <div className="text-center py-4" data-testid="reset-email-sent">
                <CheckCircle className="w-12 h-12 text-success mx-auto mb-4" />
                <h2 className="font-heading text-lg font-bold mb-2">E-Mail gesendet</h2>
                <p className="font-body text-sm text-muted-foreground mb-4">
                  Falls ein Konto mit dieser Adresse existiert, haben wir Ihnen einen Link zum Zurücksetzen gesendet.
                  Bitte prüfen Sie auch Ihren Spam-Ordner.
                </p>
                <Button variant="outline" className="font-body text-sm" onClick={() => setStep("request")} data-testid="try-again-btn">
                  Andere E-Mail-Adresse verwenden
                </Button>
              </div>
            )}

            {/* Step 3: Set new password (token from URL) */}
            {step === "set_password" && (
              <form onSubmit={confirmReset} className="space-y-4">
                <p className="font-body text-sm text-muted-foreground">
                  Wählen Sie ein neues Passwort für Ihr Konto.
                </p>
                <div>
                  <Label className="font-body text-sm">Neues Passwort</Label>
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Mindestens 8 Zeichen" className="mt-1 h-11 font-body" required data-testid="new-password-input" />
                </div>
                <div>
                  <Label className="font-body text-sm">Passwort bestätigen</Label>
                  <Input type="password" value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} placeholder="Passwort wiederholen" className="mt-1 h-11 font-body" required data-testid="confirm-password-input" />
                </div>
                <Button type="submit" className="w-full h-11 font-body bg-primary text-primary-foreground hover:bg-primary/90" disabled={loading} data-testid="confirm-reset-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Passwort ändern
                </Button>
              </form>
            )}

            {/* Step 4: Success */}
            {step === "done" && (
              <div className="text-center py-4" data-testid="reset-success">
                <CheckCircle className="w-12 h-12 text-success mx-auto mb-4" />
                <h2 className="font-heading text-lg font-bold mb-2">Passwort geändert</h2>
                <p className="font-body text-sm text-muted-foreground mb-4">
                  Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt anmelden.
                </p>
                <Button className="font-body bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate("/login")} data-testid="go-to-login-btn">
                  Zum Login
                </Button>
              </div>
            )}

            {/* Error: Token expired */}
            {step === "expired" && (
              <div className="text-center py-4" data-testid="reset-expired">
                <AlertCircle className="w-12 h-12 text-warning mx-auto mb-4" />
                <h2 className="font-heading text-lg font-bold mb-2">Link abgelaufen</h2>
                <p className="font-body text-sm text-muted-foreground mb-4">
                  Dieser Link ist nicht mehr gültig. Bitte fordern Sie einen neuen Link an.
                </p>
                <Button className="font-body bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => { setStep("request"); navigate("/reset-password", { replace: true }); }} data-testid="request-new-link-btn">
                  Neuen Link anfordern
                </Button>
              </div>
            )}

            {/* Error: Token invalid */}
            {step === "invalid" && (
              <div className="text-center py-4" data-testid="reset-invalid">
                <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                <h2 className="font-heading text-lg font-bold mb-2">Ungültiger Link</h2>
                <p className="font-body text-sm text-muted-foreground mb-4">
                  Dieser Link ist ungültig. Bitte fordern Sie einen neuen Link an.
                </p>
                <Button className="font-body bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => { setStep("request"); navigate("/reset-password", { replace: true }); }} data-testid="request-new-link-btn">
                  Neuen Link anfordern
                </Button>
              </div>
            )}

          </CardContent>
        </Card>
        <p className="text-center mt-4">
          <Link to="/login" className="font-body text-sm text-primary hover:underline" data-testid="back-to-login">
            <ArrowLeft className="w-3 h-3 inline mr-1" /> Zurück zum Login
          </Link>
        </p>
      </div>
    </div>
  );
}
